import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store, select } from '@ngrx/store';

import { Observable, Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';

import { loadSuggestedProfiles } from 'src/app/core/services/suggestions/suggestions.actions';
import {
  State as SuggestionsState,
  selectSuggestedProfiles
} from 'src/app/core/services/suggestions/suggestions.reducer';
import { Profile } from 'src/app/core/services/suggestions/suggestions.model';

import {
  addContact,
  removeContact
} from 'src/app/core/services/contacts/contacts.actions';

import {
  loadSummerProgram,
  loadAfterSchool,
  loadJobs,
  loadArticles
} from 'src/app/core/services/eduniverse/eduniverse.actions';
import {
  State as EduniverseState,
  selectSummerPrograms,
  selectAfterSchoolPrograms,
  selectJobs,
  selectArticles
} from 'src/app/core/services/eduniverse/eduniverse.reducer';
import {
  SummerProgram,
  Article
} from 'src/app/core/services/eduniverse/eduniverse.model';

import { loadJob } from 'src/app/core/services/job/job.actions';
import {
  State as JobState,
  selectJob
} from 'src/app/core/services/job/job.reducer';
import { Job } from 'src/app/core/services/job/job.model';

import {
  loadOrganization,
  resetOrganization
} from 'src/app/core/services/organizations/organizations.actions';
import {
  State as OrganizationState,
  selectOrganization
} from 'src/app/core/services/organizations/organizations.reducer';
import { Organization } from 'src/app/core/services/organizations/organization.model';
import { AuthService } from '../../../core/services/auth.service';
import {
  clearOrg,
  loadOrgProfile
} from '../../organization/store/organization.actions';
import { selectOrgProfile } from '../../organization/store/organization.selectors';

@Component({
  selector: 'app-job-bsr',
  templateUrl: './job-bsr.component.html',
  styleUrls: ['./job-bsr.component.scss']
})
export class JobBSRComponent implements OnInit, OnDestroy {
  job$: Observable<Job>;
  orgInfo$: Observable<Organization>;

  suggestedProfiles$: Observable<Profile[]>;
  summerPrograms$: Observable<{ totalCount: number; items: SummerProgram[] }>;
  afterSchoolPrograms$: Observable<{
    totalCount: number;
    items: SummerProgram[];
  }>;
  jobs$: Observable<{ totalCount: number; items: SummerProgram[] }>;
  articles$: Observable<{ totalCount: number; items: Article[] }>;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private jobStore: Store<JobState>,
    private store: Store,
    private auth: AuthService
  ) {
    this.job$ = this.jobStore.pipe(
      select(selectJob()),
      takeUntil(this._destroy$)
    );

    this.suggestedProfiles$ = this.store.pipe(
      select(selectSuggestedProfiles()),
      takeUntil(this._destroy$)
    );

    this.summerPrograms$ = this.store.pipe(
      select(selectSummerPrograms()),
      takeUntil(this._destroy$)
    );
    this.afterSchoolPrograms$ = this.store.pipe(
      select(selectAfterSchoolPrograms()),
      takeUntil(this._destroy$)
    );
    this.jobs$ = this.store.pipe(
      select(selectJobs()),
      takeUntil(this._destroy$)
    );
    this.orgInfo$ = this.store.pipe(
      select(selectOrgProfile),
      takeUntil(this._destroy$)
    );
    this.articles$ = this.store.pipe(
      select(selectArticles()),
      takeUntil(this._destroy$)
    );
  }

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(
        map(({ dashed }) => {
          const [id] = dashed.split('-');

          return { id: <string>id };
        }),
        filter((params) => !!params.id),
        map(({ id }) => {
          this.store.dispatch(resetOrganization());
          this.jobStore.dispatch(loadJob({ id }));

          if (!this.auth.isFullyAuthorized) return; // #Return#

          this.store.dispatch(loadSuggestedProfiles({ N: 3 }));
          this.store.dispatch(loadSummerProgram({ page: 1, term: '' }));
          this.store.dispatch(loadAfterSchool({ page: 1, term: '' }));
          this.store.dispatch(loadJobs({ page: 1, term: '' }));
          this.store.dispatch(
            loadArticles({ category: undefined, page: 1, term: '' })
          );
        }),
        takeUntil(this._destroy$)
      )
      .subscribe((_) => _);

    this.job$
      .pipe(
        filter((job) => !!job),
        map((job) =>
          this.store.dispatch(
            loadOrgProfile.init({ payload: { id: job.user_id } })
          )
        ),
        takeUntil(this._destroy$)
      )
      .subscribe((_) => _);
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();

    this.store.dispatch(clearOrg());
  }

  onProfileConnect(id: number) {
    this.store.dispatch(addContact({ id }));
  }

  onProfileDisconnect(id: number) {
    this.store.dispatch(removeContact({ id }));
  }
}
